import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPatch, apiPost, getApiError } from '../../lib/api';
import { downloadBlob, PDF_MIME } from '../../lib/download';
import { Button } from '../../components/Button';
import { EmailDocButton } from '../../components/EmailDocButton';
import { Input } from '../../components/Input';
import { PageScaffold } from '../../components/PageScaffold';
import { StatusPill } from '../../components/StatusPill';
import { FirmnessBadge } from '../../components/FirmnessBadge';
import { useHasPermission } from '../../lib/permissions';
import { OpsCalendar } from './OpsCalendar';
import { RescheduleModal, type RescheduleSubmit } from './RescheduleModal';
import { addDaysUTC, isoDayUTC, taskTone, taskStatusLabel, todayIsoUTC } from './opsShared';

interface MfgRow {
  taskId: string;
  orderLineId: string;
  status: string;
  qty: number;
  orderId: string;
  invoice: string;
  orderStatus: string;
  shop: string;
  customer: string;
  phone: string;
  product: string;
  sku: string;
  color: string | null;
  sideColor: string | null;
  density: string | null;
  productionDate: string | null;
  productionSplit: { date: string; qty: number }[];
  deliveryDate: string | null;
  readyAt: string | null;
  orderReady: boolean;
  pendingSupplier: number;
  pendingManufacture: number;
  productionBlockedReason: 'payment_short' | null;
}

interface LoadRow {
  date: string;
  units: number;
  cap: number;
}

const TABS = [
  { key: 'all', label: 'All' },
  { key: 'PENDING', label: 'To produce' },
  { key: 'READY', label: 'Produced' },
] as const;

// Plain-English summary of what's still holding a produced order out of delivery.
function blockerSummary(r: MfgRow): string {
  const parts: string[] = [];
  if (r.pendingSupplier > 0)
    parts.push(`${r.pendingSupplier} supplier item${r.pendingSupplier > 1 ? 's' : ''}`);
  if (r.pendingManufacture > 0) parts.push(`${r.pendingManufacture} more to produce`);
  return parts.join(' & ') || 'other items';
}

export function ManufacturingPage(): JSX.Element {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [tab, setTab] = useState<(typeof TABS)[number]['key']>('all');
  const [search, setSearch] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const canReschedule = useHasPermission('orders.reschedule');
  const canManage = useHasPermission('operations.manage');

  // Reschedule modal state — opened by clicking a job or after a rejected drag.
  const [modal, setModal] = useState<{
    row: MfgRow;
    date: string;
    error: string | null;
    force: boolean;
  } | null>(null);

  const list = useQuery({
    queryKey: ['ops-manufacturing'],
    queryFn: () => apiGet<MfgRow[]>('/operations/manufacturing'),
  });

  // Production load across a wide window powers the per-day capacity badge.
  const load = useQuery({
    queryKey: ['ops-production-load'],
    queryFn: () =>
      apiGet<LoadRow[]>('/scheduling/production-load', {
        from: isoDayUTC(addDaysUTC(new Date(), -31)),
        to: isoDayUTC(addDaysUTC(new Date(), 120)),
      }),
  });
  const loadMap = useMemo(() => {
    const m = new Map<string, { used: number; cap: number }>();
    for (const r of load.data ?? []) m.set(r.date, { used: r.units, cap: r.cap });
    return m;
  }, [load.data]);

  const produce = useMutation({
    mutationFn: (taskId: string) => apiPost(`/operations/manufacturing/${taskId}/produced`),
    onSuccess: () => {
      setErr(null);
      qc.invalidateQueries({ queryKey: ['ops-manufacturing'] });
      qc.invalidateQueries({ queryKey: ['ops-production-load'] });
    },
    onError: (e) => setErr(getApiError(e).message),
  });

  const reschedule = useMutation({
    mutationFn: (vars: { row: MfgRow; date: string; override?: boolean; reason?: string }) =>
      apiPatch(`/orders/${vars.row.orderId}/production`, {
        orderLineId: vars.row.orderLineId,
        productionDate: vars.date,
        override: vars.override,
        reason: vars.reason,
      }),
    onSuccess: () => {
      setErr(null);
      setModal(null);
      qc.invalidateQueries({ queryKey: ['ops-manufacturing'] });
      qc.invalidateQueries({ queryKey: ['ops-production-load'] });
    },
    onError: (e, vars) => {
      const msg = getApiError(e).message;
      // A direct drag attempt (no override) that the server rejected → open the
      // modal so the user can supply a reason and force it. An override attempt
      // that still failed → surface the message inside the open modal.
      setModal((cur) =>
        cur && cur.row.orderLineId === vars.row.orderLineId
          ? { ...cur, error: msg }
          : { row: vars.row, date: vars.date, error: msg, force: true },
      );
    },
  });

  const counts = useMemo(() => {
    const rows = list.data ?? [];
    return {
      all: rows.length,
      PENDING: rows.filter((r) => r.status === 'PENDING').length,
      READY: rows.filter((r) => r.status === 'READY').length,
    };
  }, [list.data]);

  const filtered = useMemo(() => {
    let rows = list.data ?? [];
    if (tab !== 'all') rows = rows.filter((r) => r.status === tab);
    const q = search.trim().toLowerCase();
    if (q) {
      rows = rows.filter(
        (r) =>
          r.customer.toLowerCase().includes(q) ||
          r.invoice.toLowerCase().includes(q) ||
          r.product.toLowerCase().includes(q) ||
          r.sku.toLowerCase().includes(q),
      );
    }
    return rows;
  }, [list.data, tab, search]);

  const downloadDay = (day: string | null): void => {
    if (!day) return;
    void downloadBlob(
      '/operations/manufacturing/day-sheet',
      { date: day },
      PDF_MIME,
      `manufacturing-${day}.pdf`,
    );
  };

  const openReschedule = (row: MfgRow): void => {
    if (!canReschedule || row.status !== 'PENDING') return;
    setModal({ row, date: row.productionDate ?? todayIsoUTC(), error: null, force: false });
  };

  const submitModal = (args: RescheduleSubmit): void => {
    if (!modal) return;
    reschedule.mutate({
      row: modal.row,
      date: args.date,
      override: args.override,
      reason: args.reason,
    });
  };

  return (
    <PageScaffold
      title="Manufacturing"
      subtitle={
        <>
          Local mattresses to make, by production day. Mark each item produced once it leaves the
          factory floor — the order moves to delivery only when every made-to-order &amp; supplier
          item is ready.
          {canReschedule && ' Drag a job to another day (or click it) to re-plan production.'}
        </>
      }
    >
      {err && (
        <div className="rounded border border-negative/30 bg-negative-soft text-negative text-sm px-4 py-2">
          {err}
        </div>
      )}

      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="inline-flex items-center gap-0.5 rounded-md border border-rule bg-surface p-0.5">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={
                'px-3 py-1.5 rounded text-sm transition ' +
                (tab === t.key
                  ? 'bg-accent-soft text-accent font-medium'
                  : 'text-ink-2 hover:bg-paper-2')
              }
            >
              {t.label}
              <span className="ml-1.5 text-xs text-ink-3">{counts[t.key]}</span>
            </button>
          ))}
        </div>
        <div className="w-64">
          <Input
            placeholder="Search invoice, customer, product…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {list.isLoading ? (
        <div className="text-center text-ink-3 py-10">Loading…</div>
      ) : (
        <OpsCalendar<MfgRow>
          rows={filtered}
          getDay={(r) => r.productionDate}
          getId={(r) => r.taskId}
          canMove={(r) => canReschedule && r.status === 'PENDING'}
          onMove={(row, targetIso) => reschedule.mutate({ row, date: targetIso })}
          onClickItem={openReschedule}
          capacityFor={(iso) => loadMap.get(iso) ?? null}
          emptyLabel="Nothing to manufacture on this day."
          dayHeaderAction={(iso) => (
            <div className="flex items-center gap-1">
              <Button variant="secondary" size="sm" onClick={() => downloadDay(iso)}>
                Day sheet · PDF
              </Button>
              <EmailDocButton
                endpoint="/operations/manufacturing/day-sheet"
                method="get"
                params={{ date: iso }}
                selfDefault
                buttonVariant="secondary"
                buttonSize="sm"
                title="Email manufacturing day sheet"
                subtitle="Send the manufacturing worksheet PDF by email."
              />
            </div>
          )}
          renderChip={(r, { view }) => {
            const compact = view === 'month';
            return (
              <div className="space-y-1">
                <div>
                  <StatusPill tone={taskTone(r.status)}>
                    {taskStatusLabel(r.status, 'MANUFACTURE')}
                  </StatusPill>
                </div>
                <div className="text-[13px] text-ink font-medium leading-tight break-words">
                  {r.customer}
                </div>
                <div className="kc-mono text-[11px] text-ink-3 leading-tight" title={r.invoice}>
                  {r.invoice}
                </div>
                <div className="text-xs text-ink-2 leading-tight">
                  {r.qty}× {r.product}
                </div>
                <FirmnessBadge value={r.density} />
                {!compact && (
                  <>
                    {(r.color || r.sideColor) && (
                      <div className="text-[11px] text-ink-3">
                        {[r.color, r.sideColor].filter(Boolean).join(' / ')}
                      </div>
                    )}
                    <div className="text-[11px] text-ink-3">Deliver by {r.deliveryDate ?? '—'}</div>
                    {r.status === 'READY' && !r.orderReady && (
                      <div className="text-[11px] text-warning">
                        Produced — held until {blockerSummary(r)} ready (Purchase Orders page)
                      </div>
                    )}
                    {r.productionBlockedReason === 'payment_short' && (
                      <div className="text-[11px] text-negative">awaiting finance verification</div>
                    )}
                    <div className="flex flex-wrap items-center gap-1.5 pt-2 mt-1 border-t border-rule-2">
                      {r.status === 'PENDING' && canManage && (
                        <Button
                          type="button"
                          variant="primary"
                          size="xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            produce.mutate(r.taskId);
                          }}
                          title={
                            r.productionBlockedReason === 'payment_short'
                              ? 'Finance must verify the deposit before this can go into production'
                              : undefined
                          }
                          disabled={
                            r.productionBlockedReason === 'payment_short' ||
                            (produce.isPending && produce.variables === r.taskId)
                          }
                        >
                          Mark produced ✓
                        </Button>
                      )}
                      <Button
                        type="button"
                        variant="secondary"
                        size="xs"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/orders/${r.orderId}`);
                        }}
                      >
                        Open order
                      </Button>
                    </div>
                  </>
                )}
              </div>
            );
          }}
        />
      )}

      {modal && (
        <RescheduleModal
          title="Reschedule production"
          subtitle={`${modal.row.invoice} · ${modal.row.qty}× ${modal.row.product}`}
          initialDate={modal.date}
          minDate={todayIsoUTC()}
          busy={reschedule.isPending}
          error={modal.error}
          forceOverride={modal.force}
          onClose={() => setModal(null)}
          onSubmit={submitModal}
        />
      )}
    </PageScaffold>
  );
}
